import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Award, Save } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : 'http://localhost:8001/api';

const AdminCertificateDesigner = () => {
  const { token, user } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [template, setTemplate] = useState({
    logo_base64: '',
    signature_base64: '',
    background_color: '#FFFFFF',
    text_color: '#000000',
    institution_name: 'SkillEdge Platform',
    signatory_name: 'Director',
    signatory_title: 'Chief Education Officer',
  });

  useEffect(() => {
    if (!token || user?.role !== 'admin') {
      navigate('/login');
      return;
    }
    fetchTemplate();
  }, [token, user, navigate]);

  const fetchTemplate = async () => {
    try {
      const response = await axios.get(`${API}/certificates/admin/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (response.data.template) {
        setTemplate(prev => ({
          ...prev,
          ...response.data.template
        }));
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      toast.error('Failed to load certificate template');
    }
  };

  const handleFileUpload = (field) => (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setTemplate(prev => ({
        ...prev,
        [field]: reader.result
      }));
      toast.success('Image uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    try {
      await axios.post(
        `${API}/certificates/admin/template`,
        template,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Certificate template saved successfully!');
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save certificate template');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Certificate Designer</h1>
          <p className="text-gray-600">Customize your course completion certificates</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Institution Logo</CardTitle>
                <CardDescription>Upload your institution's logo (max 5MB)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="logo" className="cursor-pointer">
                    <div className="border-2 border-dashed rounded-lg p-6 hover:border-indigo-500 transition-colors text-center">
                      {template.logo_base64 ? (
                        <div className="space-y-2">
                          <img
                            src={template.logo_base64}
                            alt="Logo preview"
                            className="max-h-32 mx-auto object-contain"
                          />
                          <p className="text-sm text-gray-600">Click to change logo</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-12 w-12 mx-auto text-gray-400" />
                          <p className="text-sm text-gray-600">Click to upload logo</p>
                        </div>
                      )}
                    </div>
                  </Label>
                  <Input
                    id="logo"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload('logo_base64')}
                  />
                </div>
                {template.logo_base64 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTemplate(prev => ({ ...prev, logo_base64: '' }))}
                  >
                    Remove Logo
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Signatory Signature</CardTitle>
                <CardDescription>Upload the authorized signatory's signature (max 5MB)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="signature" className="cursor-pointer">
                    <div className="border-2 border-dashed rounded-lg p-6 hover:border-indigo-500 transition-colors text-center">
                      {template.signature_base64 ? (
                        <div className="space-y-2">
                          <img
                            src={template.signature_base64}
                            alt="Signature preview"
                            className="max-h-24 mx-auto object-contain"
                          />
                          <p className="text-sm text-gray-600">Click to change signature</p>
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <Upload className="h-12 w-12 mx-auto text-gray-400" />
                          <p className="text-sm text-gray-600">Click to upload signature</p>
                        </div>
                      )}
                    </div>
                  </Label>
                  <Input
                    id="signature"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileUpload('signature_base64')}
                  />
                </div>
                {template.signature_base64 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setTemplate(prev => ({ ...prev, signature_base64: '' }))}
                  >
                    Remove Signature
                  </Button>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Certificate Colors</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="bg_color">Background Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="bg_color"
                        type="color"
                        value={template.background_color}
                        onChange={(e) => setTemplate(prev => ({ ...prev, background_color: e.target.value }))}
                        className="w-20 h-10"
                      />
                      <Input
                        value={template.background_color}
                        onChange={(e) => setTemplate(prev => ({ ...prev, background_color: e.target.value }))}
                        placeholder="#FFFFFF"
                        className="flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="text_color">Text Color</Label>
                    <div className="flex gap-2">
                      <Input
                        id="text_color"
                        type="color"
                        value={template.text_color}
                        onChange={(e) => setTemplate(prev => ({ ...prev, text_color: e.target.value }))}
                        className="w-20 h-10"
                      />
                      <Input
                        value={template.text_color}
                        onChange={(e) => setTemplate(prev => ({ ...prev, text_color: e.target.value }))}
                        placeholder="#000000"
                        className="flex-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Certificate Text</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="institution">Institution Name</Label>
                  <Input
                    id="institution"
                    value={template.institution_name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, institution_name: e.target.value }))}
                    placeholder="SkillEdge Platform"
                  />
                </div>
                <div>
                  <Label htmlFor="signatory_name">Signatory Name</Label>
                  <Input
                    id="signatory_name"
                    value={template.signatory_name}
                    onChange={(e) => setTemplate(prev => ({ ...prev, signatory_name: e.target.value }))}
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label htmlFor="signatory_title">Signatory Title</Label>
                  <Input
                    id="signatory_title"
                    value={template.signatory_title}
                    onChange={(e) => setTemplate(prev => ({ ...prev, signatory_title: e.target.value }))}
                    placeholder="Chief Education Officer"
                  />
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleSave}
              className="w-full"
              size="lg"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Certificate Template
            </Button>
          </div>

          <div>
            <Card className="sticky top-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-5 w-5" />
                  Certificate Preview
                </CardTitle>
                <CardDescription>This is how your certificates will look</CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  className="border-4 border-gray-800 rounded-lg p-8 aspect-[1.414/1] flex flex-col justify-center items-center text-center"
                  style={{
                    backgroundColor: template.background_color,
                    color: template.text_color
                  }}
                >
                  {template.logo_base64 && (
                    <img
                      src={template.logo_base64}
                      alt="Logo"
                      className="h-16 mb-4 object-contain"
                    />
                  )}

                  <h2 className="text-2xl font-bold mb-2">CERTIFICATE OF COMPLETION</h2>
                  
                  <p className="text-sm mb-2">This is to certify that</p>
                  
                  <p className="text-xl font-bold mb-2">Student Name</p>
                  
                  <p className="text-sm mb-2">has successfully completed the course</p>
                  
                  <p className="text-lg font-semibold mb-3">Course Title</p>
                  
                  <p className="text-xs mb-4">Completed on {new Date().toLocaleDateString()}</p>
                  
                  <p className="text-sm font-semibold mb-6">{template.institution_name}</p>
                  
                  {template.signature_base64 && (
                    <img
                      src={template.signature_base64}
                      alt="Signature"
                      className="h-12 mb-1 object-contain"
                    />
                  )}
                  
                  <div className="border-t border-current w-32 mb-1"></div>
                  <p className="text-xs font-medium">{template.signatory_name}</p>
                  <p className="text-xs">{template.signatory_title}</p>
                  
                  <p className="text-xs mt-4 opacity-50">Certificate ID: CERT-XXXX</p>
                </div>

                <div className="mt-4 p-3 bg-blue-50 rounded-md text-sm text-blue-800">
                  <p className="font-medium mb-1">💡 Preview Note:</p>
                  <p>This is a simplified preview. The actual PDF certificate will have better formatting and quality.</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCertificateDesigner;
